import { useMutation, useQuery } from "@apollo/client";
import {
  CREATE_SHIFT_TEMPLATE_MUTATION,
  DELETE_SHIFT_TEMPLATE_MUTATION,
  DUPLICATE_SHIFT_TEMPLATE_MUTATION,
  GET_SHIFT_TEMPLATE_QUERY,
  GET_SHIFT_TEMPLATES_QUERY,
  UPDATE_SHIFT_TEMPLATE_MUTATION,
} from "@/graphql/shift-templates";
import { useAsyncAction } from "@/lib/hooks/use-async-action";

interface CreateTemplateData {
  name: string;
  description?: string;
  position?: string;
  scheduleType: string;
  days: number[];
  color?: string;
  icon?: string;
  timeIntervals: Array<{
    startTime: string;
    endTime: string;
  }>;
  breakRules: Array<{
    afterHours: number;
    breakType: string;
    duration: number;
    isPaid: boolean;
    isRequired?: boolean;
  }>;
  sortOrder?: number;
}

export function useShiftTemplatesGraphQL(franchiseId: string) {
  // Query for templates list
  const { data, loading, error, refetch } = useQuery(GET_SHIFT_TEMPLATES_QUERY, {
    variables: { franchiseId },
    skip: !franchiseId,
    errorPolicy: "all",
  });

  // Mutations
  const [createMutation] = useMutation(CREATE_SHIFT_TEMPLATE_MUTATION, {
    refetchQueries: [{ query: GET_SHIFT_TEMPLATES_QUERY, variables: { franchiseId } }],
    onError: (error) => {
      console.error("Error creating shift template:", error);
    },
  });

  const [updateMutation] = useMutation(UPDATE_SHIFT_TEMPLATE_MUTATION, {
    refetchQueries: [{ query: GET_SHIFT_TEMPLATES_QUERY, variables: { franchiseId } }],
    onError: (error) => {
      console.error("Error updating shift template:", error);
    },
  });

  const [deleteMutation] = useMutation(DELETE_SHIFT_TEMPLATE_MUTATION, {
    refetchQueries: [{ query: GET_SHIFT_TEMPLATES_QUERY, variables: { franchiseId } }],
    onError: (error) => {
      console.error("Error deleting shift template:", error);
    },
  });

  const [duplicateMutation] = useMutation(DUPLICATE_SHIFT_TEMPLATE_MUTATION, {
    refetchQueries: [{ query: GET_SHIFT_TEMPLATES_QUERY, variables: { franchiseId } }],
    onError: (error) => {
      console.error("Error duplicating shift template:", error);
    },
  });

  // Async actions for consistent API
  const createTemplate = useAsyncAction(async (templateData: CreateTemplateData) => {
    try {
      const { data, errors } = await createMutation({
        variables: {
          franchiseId,
          input: templateData,
        },
      });

      if (errors && errors.length > 0) {
        console.error("GraphQL errors:", errors);
        throw new Error(errors[0].message);
      }

      if (!data?.createShiftTemplate) {
        throw new Error("No data returned from createShiftTemplate mutation");
      }
      return data.createShiftTemplate;
    } catch (error) {
      console.error("Error in createTemplate:", error);
      throw error;
    }
  });

  const updateTemplate = useAsyncAction(
    async (templateId: string, templateData: Partial<CreateTemplateData>) => {
      const { data } = await updateMutation({
        variables: {
          id: templateId,
          input: templateData,
        },
      });
      return data?.updateShiftTemplate;
    }
  );

  const deleteTemplate = useAsyncAction(async (templateId: string) => {
    const { data } = await deleteMutation({
      variables: {
        id: templateId,
      },
    });
    return data?.deleteShiftTemplate;
  });

  const duplicateTemplate = useAsyncAction(async (templateId: string, newName: string) => {
    const { data } = await duplicateMutation({
      variables: {
        id: templateId,
        newName,
      },
    });
    return data?.duplicateShiftTemplate;
  });

  return {
    templates: data?.getShiftTemplates || [],
    loading,
    error,
    refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  };
}

// Hook for getting a single template
export function useShiftTemplate(templateId: string) {
  const { data, loading, error, refetch } = useQuery(GET_SHIFT_TEMPLATE_QUERY, {
    variables: { id: templateId },
    skip: !templateId,
    errorPolicy: "all",
  });

  return {
    template: data?.getShiftTemplate,
    loading,
    error,
    refetch,
  };
}
